import React, { useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useGetAuthenticatedUser } from '../../../Flux/Selector/index.js'
import { isGlobalAdmin } from '../../../Model/User/IUser.js'
import type { StatRow } from '../../../Repository/Admin/AdminStatsRepository.js'
import { metricById, metricDateKey, type Metric } from './metrics.js'
import { availableMonths, filterByPeriod, monthlySeries, periodLabel, type Period } from './statsAggregations.js'
import { StatsBarChart } from './StatsBarChart.js'
import { StatsLineChart } from './StatsLineChart.js'
import { PeriodPicker, SummaryLine } from './StatsUi.js'
import { useStatsData } from './useStatsData.js'

/** Render a metric over an already period-filtered row set (value for kpi, chart otherwise).
 *  `yMax` fixes the axis so side-by-side period versions are visually comparable. */
const renderMetric = (metric: Metric, rows: StatRow[], yMax?: number) => {
  if (metric.kind === 'kpi') return <div className="text-4xl font-bold text-gray-800">{metric.value(rows)}</div>
  if (metric.kind === 'line') return <StatsLineChart data={metric.data(rows)} color={metric.color} zoom height={360} yMax={yMax} />
  return (
    <>
      {metric.summary && <SummaryLine summary={metric.summary(rows)} />}
      <StatsBarChart data={metric.data(rows)} color={metric.color} rotateLabels={metric.rotateLabels} height={360} yMax={yMax} />
    </>
  )
}

export const StatsMetricPage = () => {
  const user = useGetAuthenticatedUser()
  const { id } = useParams()
  const metric = metricById(id)
  const [excludeInternal, setExcludeInternal] = useState(true)
  const [versions, setVersions] = useState<Period[]>([{ kind: 'all' }])
  const { orders, projects, loading, error } = useStatsData(excludeInternal)

  if (!user) return <></>
  if (!isGlobalAdmin(user)) return <Navigate to="/home" replace />
  if (!metric) return <Navigate to="/admin/stats" replace />

  const rows = metric.scope === 'orders' ? orders : projects
  const dateKey = metricDateKey(metric.scope)
  const months = availableMonths(rows, dateKey)
  const defaultMonth = months[months.length - 1]

  const addVersion = (): void => setVersions([...versions, defaultMonth ? { kind: 'month', month: defaultMonth } : { kind: 'all' }])
  const updateVersion = (i: number, p: Period): void => setVersions(versions.map((v, idx) => (idx === i ? p : v)))
  const removeVersion = (i: number): void => setVersions(versions.filter((_, idx) => idx !== i))
  const insertMonthVersion = (index: number, month: string): void => {
    const next = [...versions]
    next.splice(index, 0, { kind: 'month', month })
    setVersions(next)
  }
  const adjacentMonth = (month: string, delta: number): string | undefined => {
    const next = months.indexOf(month) + delta
    return next >= 0 && next < months.length ? months[next] : undefined
  }

  // Shared y-axis max across all version panels so bar heights are comparable between periods.
  const sharedMax =
    metric.kind === 'kpi'
      ? undefined
      : (() => {
          const values = versions.flatMap((p) => metric.data(filterByPeriod(rows, dateKey, p)).map((d) => d.value))
          return values.length ? Math.max(...values) || undefined : undefined
        })()

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/admin/stats" className="link link-primary text-sm">
            ← Stats
          </Link>
          <h1 className="text-2xl font-bold">{metric.title}</h1>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" className="toggle toggle-sm" checked={excludeInternal} onChange={(e) => setExcludeInternal(e.target.checked)} />
          Exclude internal orgs
        </label>
      </header>

      {loading && <p className="text-gray-500">Loading…</p>}
      {error && <p className="text-error">Could not load stats: {error}</p>}

      {!loading && !error && (
        <>
          {metric.kind === 'kpi' && metric.monthly && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold text-gray-700 mb-2">Trend (by month)</h3>
              <StatsLineChart data={monthlySeries(rows, dateKey, metric.monthly)} zoom height={360} />
            </div>
          )}

          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-700">Compare periods</h3>
            <button className="btn btn-sm" onClick={addVersion}>
              + Add version
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {versions.map((period, i) => {
              const scoped = filterByPeriod(rows, dateKey, period)
              const prevMonth = period.kind === 'month' ? adjacentMonth(period.month, -1) : undefined
              const nextMonth = period.kind === 'month' ? adjacentMonth(period.month, 1) : undefined
              return (
                <div key={i} className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    {prevMonth ? (
                      <button className="btn btn-ghost btn-xs" onClick={() => insertMonthVersion(i, prevMonth)} title={`Add ${prevMonth} (left)`}>
                        ‹ {prevMonth}
                      </button>
                    ) : (
                      <span />
                    )}
                    <PeriodPicker
                      months={months}
                      period={period}
                      onChange={(p) => updateVersion(i, p)}
                      onRemove={versions.length > 1 ? () => removeVersion(i) : undefined}
                    />
                    {nextMonth ? (
                      <button className="btn btn-ghost btn-xs" onClick={() => insertMonthVersion(i + 1, nextMonth)} title={`Add ${nextMonth} (right)`}>
                        {nextMonth} ›
                      </button>
                    ) : (
                      <span />
                    )}
                  </div>
                  {renderMetric(metric, scoped, sharedMax)}
                  <div className="text-xs text-gray-400 mt-1">
                    {periodLabel(period)} · {scoped.length} {metric.scope}
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
